import plur from "plur";
import * as yup from "yup";
import { Button, Flex, ModalV2, Text } from "@jsq/ds/core";
import { Form, FormSelect } from "@jsq/ds/forms";
import { SPACING } from "~/styles/variables";
import { WorkspaceData } from "../../types";
import { useRemoveUserModal } from "../RemoveUserModal/hooks";
import { FormValues, initialValues, useFetchUsersWithArenaAccess } from "./hook";

type RemoveUserModalProps = {
  isOpen: boolean;
  onClose: () => void;
  selectedWorkspaces?: WorkspaceData[];
};

const validationSchema = yup.object().shape({
  users: yup.array().of(yup.string()).min(1, "Please select at least one user"),
});

const RemoveUserBulkModal = ({ selectedWorkspaces, isOpen, onClose }: RemoveUserModalProps) => {
  const { endArenaAccess, loading } = useRemoveUserModal();
  const { users } = useFetchUsersWithArenaAccess({
    arenaIds: selectedWorkspaces?.map((w) => w.id) ?? [],
  });

  const handleRemoveUser = async ({ users }: FormValues) => {
    await endArenaAccess({
      arenaIds: selectedWorkspaces?.map((w) => w.id) ?? [],
      userIds: users ?? [],
    });
    onClose();
  };

  return (
    <ModalV2
      data-testid="remove-user-modal"
      title="Remove user from workspace"
      description={`${selectedWorkspaces?.length} ${plur("workspace", selectedWorkspaces?.length ?? 0)}`}
      show={isOpen}
      onHide={onClose}
    >
      <ModalV2.Body>
        <Form
          initialValues={initialValues}
          // @ts-expect-error the onClick function won't accept the handleSubmit from formik. Another DS issue
          onSubmit={(values) => handleRemoveUser(values)}
          yupValidationSchema={validationSchema}
        >
          <Flex flexDirection="column" gap={SPACING.LARGE}>
            <Flex flexDirection="column" gap={SPACING.X_SMALL}>
              <Text>Users assigned to selected workspaces</Text>
              <FormSelect
                full
                multiple
                name="users"
                placeholder="Select users..."
                matchFromStart={false}
              >
                <FormSelect.Section>
                  {users.map((r) => (
                    <FormSelect.Option value={r.id} key={r.id}>
                      {`${r.name} (${r.email})`}
                    </FormSelect.Option>
                  ))}
                </FormSelect.Section>
              </FormSelect>
            </Flex>
            <Text variant="regular-lg" marginBottom="xl">
              Any active work assigned to selected users will need to be unassigned.
            </Text>
          </Flex>
          <Flex gap={SPACING.MEDIUM} justifyContent="end">
            <Button variant="neutral" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" variant="danger" isLoading={loading} disabled={loading}>
              {`Remove from ${selectedWorkspaces?.length} ${plur("workspace", selectedWorkspaces?.length ?? 0)}`}
            </Button>
          </Flex>
        </Form>
      </ModalV2.Body>
    </ModalV2>
  );
};

export default RemoveUserBulkModal;
